import { embeddedArtworks } from './embeddedArtworks'
import { BASE_CURRENCY } from '../lib/currency'

export interface SizeOption {
    name: string
    price: number
}

export interface ArtworkPreview {
    id: number
    title: string
    category: string
    price: number
    available: boolean
    image: string
}

export interface ArtworkDetail extends ArtworkPreview {
    description: string
    medium: string
    year: string
    sizes: SizeOption[]
    images: string[]
}

export interface CategorySummary {
    name: string
    count: number
}

export interface FooterLink {
    label: string
    page: string
}

const currency = BASE_CURRENCY

const featuredOilHero = '/media/product-1763659580_w1200.webp'
const secondaryOilImages = [
    '/media/product-1763657095299_w1200.webp',
    '/media/product-1763871827648_w1200.webp',
]
const featuredInkHero = '/media/product-1763657331_w1200.webp'
const secondaryInkImages = [
    '/media/product-1763871388165_w1200.webp',
    '/media/product-1763659437680_w1200.webp',
    '/media/product-1763657118840_w1200.webp',
]

const previewFromEmbedded = (artwork: typeof embeddedArtworks[number]): ArtworkPreview => {
    const image = artwork.imageUrl || artwork.images?.[0] || ''
    return {
        id: artwork.id,
        title: artwork.title,
        category: artwork.category,
        price: artwork.price,
        available: Boolean(artwork.available),
        image,
    }
}

const baseArtworks: ArtworkPreview[] = embeddedArtworks.map(previewFromEmbedded)

const sizeBlueprint: Array<{ name: string; delta: number }> = [
    { name: '8" x 10"', delta: 0 },
    { name: '12" x 16"', delta: 350 },
    { name: '16" x 20"', delta: 620 },
    { name: '20" x 28"', delta: 920 },
]

const detailByArtwork: Record<number, ArtworkDetail> = embeddedArtworks.reduce(
    (acc, artwork) => {
        const preview = previewFromEmbedded(artwork)
        const images =
            (artwork.images && artwork.images.length > 0
                ? artwork.images
                : preview.image
                    ? [preview.image]
                    : []) as string[]
        acc[artwork.id] = {
            ...preview,
            description:
                artwork.description ||
                `“${artwork.title}” explores quiet narratives through layered brushwork and luminous glazes. Each piece is painted alla prima, keeping gestures present in the final surface.`,
            medium: artwork.medium || 'Oil on canvas',
            year: artwork.year || '2025',
            sizes: sizeBlueprint.map((size) => ({
                name: size.name,
                price: artwork.price + size.delta,
            })),
            images,
        }
        return acc
    },
    {} as Record<number, ArtworkDetail>,
)

const categories: CategorySummary[] = [
    { name: 'Portraits', count: 18 },
    { name: 'Landscapes', count: 14 },
    { name: 'Still Life', count: 16 },
    { name: 'Animals', count: 9 },
    { name: 'Sketches', count: 20 },
]

const footerLinks: FooterLink[] = [
    // { label: 'Search', page: 'search' },
    { label: 'Customer Support', page: 'support' },
    { label: 'Shipping Policy', page: 'shipping-policy' },
    { label: 'Refund Policy', page: 'refund-policy' },
    { label: 'Legal Notice', page: 'legal-notice' },
    { label: 'Privacy Policy', page: 'privacy-policy' },
]

export const siteContent = {
    currency,
    brand: {
        name: 'Daria Chizh Art Studio',
        scriptWordmark: 'ChizhArt',
        discipline: 'Contemporary Realism',
        heroTitle: 'Original Artworks',
        heroSubtitle:
            'Discover expressive portraits and soulful pet commissions crafted in unique signature palette.',
    },
    navigation: ['Home', 'Artworks', 'Commission', 'Reviews', 'Blog', 'Contact', 'About'],
    featuredProduct: {
        slug: 'oil',
//     eyebrow: 'Custom Pet Portrait',
        title: 'Custom Pet Portrait Oil Painting',
        description:
            'Commission a bespoke portrait hand-painted. Every piece includes a collaborative brief, sketch approvals, and archival finishing with varnish.',
        baseImage: featuredOilHero,
        images: [featuredOilHero, ...secondaryOilImages],
        sizes: [
            { name: '6" x 6"', price: 380 },
            { name: '6" x 8"', price: 420 },
            { name: '8" x 10"', price: 500 },
            { name: '10" x 12"', price: 620 },
            { name: '12" x 16"', price: 780 },
            { name: '16" x 20"', price: 980 },
            { name: '20" x 28"', price: 1320 },
        ],
        shippingNote: '* free worldwide shipping on every commission',
    },
    featuredInkProduct: {
        slug: 'ink',
//     eyebrow: 'Expressive Ink Portrait',
        title: 'Custom Ink Painting',
        description:
            'Monochrome ink portraits with gestural mark making—ideal for sketchbook-style keepsakes or gifts that travel light.',
        baseImage: featuredInkHero,
        images: [featuredInkHero, ...secondaryInkImages],
        sizes: [
            { name: '5" x 7"', price: 180 },
            { name: '8" x 10"', price: 240 },
            { name: '11" x 14"', price: 310 },
            { name: '12" x 16"', price: 360 },
        ],
        shippingNote: '* ships flat worldwide in a rigid mailer',
    },
    gallery: {
        artworks: baseArtworks.slice(0, 9),
        categories,
    },
    artworks: baseArtworks,
    artworkDetails: detailByArtwork,
    footer: {
        links: footerLinks,
        socials: [
            { label: 'Instagram', icon: 'instagram', href: 'https://www.instagram.com/chizh_art' },
            { label: 'Facebook', icon: 'facebook', href: 'https://www.facebook.com/profile.php?id=100046683215826' },
            { label: 'YouTube', icon: 'youtube', href: 'https://youtube.com' },
        ],
        copyright: '© 2026 Daria Chizh Art Studio — All rights reserved.',
    },
}
